import { useEffect, useRef, useState } from "react";
import { Upload, Loader2, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const COLORS = [["primary_color", "Background"], ["secondary_color", "Panels & tiles"], ["accent_color", "Highlight"]];
const URLS = [
  ["roku_feed_url", "Roku feed URL", "https://…/roku/….json"],
  ["firetv_feed_url", "Amazon Fire TV feed URL", "https://…/firetv/….xml"],
  ["chromecast_feed_url", "Chromecast feed URL", "https://…/chromecast/….json"],
  ["hls_url", "HLS live stream URL", "https://…/live/….m3u8"],
];

function ColorField({ k, label, value, set }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <div className="space-y-1.5">
      <Label className="text-gray-300 text-xs">{label}</Label>
      <div className="flex items-center gap-2 rounded-lg bg-gray-950 border border-gray-800 pl-1.5 pr-2 h-10">
        <input data-testid={`color-${k}-picker`} type="color" value={value} onChange={(e) => set(k, e.target.value.toUpperCase())}
          className="h-7 w-8 cursor-pointer rounded border-0 bg-transparent p-0" />
        <input data-testid={`color-${k}-text`} value={text} maxLength={7}
          onChange={(e) => { setText(e.target.value); if (/^#[0-9a-f]{6}$/i.test(e.target.value)) set(k, e.target.value.toUpperCase()); }}
          className="w-full bg-transparent font-mono text-sm text-white outline-none" />
      </div>
    </div>
  );
}

function LogoField({ value, set }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/channel-builder/logo", fd);
      set("logo_url", r.data.logo_url);
    } catch (err) { toast.error(err?.response?.data?.detail || "Logo upload failed"); } finally { setBusy(false); }
  };
  const src = value?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value;
  return (
    <div className="space-y-1.5">
      <Label className="text-gray-300 text-xs">Logo <span className="text-gray-500">(PNG with a transparent background looks best)</span></Label>
      <div className="flex items-center gap-3">
        <div className="h-16 w-32 rounded-lg border border-dashed border-gray-700 bg-[repeating-conic-gradient(#1f2937_0%_25%,#111827_0%_50%)] bg-[length:12px_12px] flex items-center justify-center overflow-hidden">
          {value ? <img data-testid="channel-logo-preview" src={src} alt="" className="max-h-full max-w-full object-contain" /> : <span className="text-[10px] text-gray-500">No logo</span>}
        </div>
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} data-testid="channel-logo-file" />
        <button type="button" data-testid="channel-logo-upload" onClick={() => ref.current?.click()} disabled={busy}
          className="h-9 px-3 rounded-lg border border-gray-800 text-xs text-gray-300 hover:text-amber-400 hover:border-amber-500/40 inline-flex items-center gap-1.5 transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {value ? "Replace" : "Upload"}
        </button>
        {value && <button type="button" data-testid="channel-logo-remove" onClick={() => set("logo_url", "")} className="text-gray-500 hover:text-red-400" title="Remove logo"><X className="h-4 w-4" /></button>}
      </div>
    </div>
  );
}

export function ChannelForm({ pkg, set }) {
  return (
    <div className="space-y-5" data-testid="channel-form">
      <div className="space-y-1.5">
        <Label className="text-gray-300 text-xs">Church name (shown as the channel name)</Label>
        <Input data-testid="channel-name-input" value={pkg.church_name} maxLength={60} onChange={(e) => set("church_name", e.target.value)} className="bg-gray-950 border-gray-800" />
      </div>
      <LogoField value={pkg.logo_url} set={set} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {COLORS.map(([k, l]) => <ColorField key={k} k={k} label={l} value={pkg[k]} set={set} />)}
      </div>
      <div className="space-y-3">
        {URLS.map(([k, l, ph]) => (
          <div key={k} className="space-y-1.5">
            <Label className="text-gray-300 text-xs">{l}</Label>
            <Input data-testid={`channel-${k}`} value={pkg[k]} placeholder={ph} onChange={(e) => set(k, e.target.value)} className="bg-gray-950 border-gray-800 font-mono text-xs" />
          </div>
        ))}
      </div>
    </div>
  );
}
